import { CoreGridMapper } from './CoreGridMapper';
import {
    ClaimedOrderGridItem,
    ClaimedOrderInterface,
    ClaimedOrderResponse,
    CoreGridInput,
    CoreOrder,
    CoreClaimsGridOutput,
    GridInput,
    GridOutput,
    ProductInterface,
    ReceivedClaimedOrderPayload,
    SkladonError,
    HTTP_STATUS,
    fromCoreClaimNames,
    toCoreClaimNames,
    Claim
} from 'skladon-types';

export class ClaimsMapper {
    public static fromCore(coreClaim: ClaimedOrderResponse): Claim {
        return {
            ...ClaimsMapper.fromCoreBase(coreClaim),
            files: coreClaim.FILES,
            products: coreClaim.PRODUCTS
                ? coreClaim.PRODUCTS.map((product) => ({
                      claimedQuantity: product.CLAIMED_QUANTITY,
                      lineState: product.LINE_STATE,
                      resendQuantity: product.RESEND_QUANTITY,
                      sku: product.SKU
                  }))
                : undefined,
            eshopOrderId: coreClaim.ESHOP_ORDER_ID || ''
        };
    }

    public static fromCoreClaimGrid(coreClaim: ClaimedOrderGridItem): Claim {
        return {
            ...ClaimsMapper.fromCoreBase(coreClaim),
            eshopOrderId: coreClaim.ESHOP_ORDER_ID,
            orderId: coreClaim.ORDER_ID ?? null,
            orderType: coreClaim.ORDER_TYPE ?? null,
            shipper: coreClaim.SHIPPER ?? null,
            trackingNumber: coreClaim.TRACKING_NUMBER ?? null,
            returnTrackingNumber: coreClaim.RETURN_TRACKING_NUMBER ?? null
        };
    }

    public static toCore(
        claim: Partial<Claim>,
        coreOrder?: CoreOrder,
        coreProducts: Pick<ProductInterface, 'SKU' | 'ID'>[] = []
    ): Partial<ReceivedClaimedOrderPayload> {
        claim.files?.forEach((file) => {
            if (file.id && file.data) {
                throw new SkladonError(
                    HTTP_STATUS.BAD_REQUEST,
                    `You cannot send file with both id and data!`
                );
            }
        });
        return {
            CLAIM_CAUSE: claim.claimCause,
            CLAIM_CAUSE_OTHERS_TYPE: claim.causeOtherType,
            DESCRIPTION: claim.description,
            ESHOP_CLAIM_ID: claim.eshopClaimId,
            FILES: claim.files
                ? claim.files.map((file) => ({
                      id: file.id,
                      type: file.type,
                      data: file.data,
                      fileName: file.fileName,
                      fileType: file.fileType,
                      fileSize: file.fileSize || 'unknown'
                  }))
                : undefined,
            NOTE: claim.note,
            ORDER_ID: coreOrder?.ID,
            ESHOP_NOTE: claim.eshopNote,
            PRODUCTS: claim.products
                ? claim.products.map((product) => {
                      const foundProduct = coreProducts.find((coreProduct) => coreProduct.SKU === product.sku);
                      return {
                          PRODUCT_ID: foundProduct!.ID,
                          CLAIMED_QUANTITY: product.claimedQuantity,
                          LINE_STATE: product.lineState
                      };
                  })
                : undefined
        };
    }

    public static fromCoreGrid(grid: CoreClaimsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreClaimGrid) => ClaimsMapper.fromCoreClaimGrid(coreClaimGrid))
                .map((claim) => CoreGridMapper.omitColumns(claim, fromCoreClaimNames, grid.request.columns)),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreClaimNames, 1000),
            totalCount: grid.totalCount
        };
    }

    public static toCoreGrid(grid: GridInput): CoreGridInput {
        return CoreGridMapper.mapGrid(grid, toCoreClaimNames, 1000);
    }

    private static parsePrice(value: string | null | undefined): number | null {
        if (value == null || value === '') return null;
        const n = Number(value);
        return Number.isNaN(n) ? null : n;
    }

    private static toDateString(value: Date | string | null | undefined): string | null {
        return value ? String(value) : null;
    }

    private static fromCoreBase(coreClaim: ClaimedOrderInterface): Omit<Claim, 'eshopOrderId'> {
        return {
            id: coreClaim.ID,
            fcn: coreClaim.FCN,
            cctv: !!coreClaim.CCTV,
            causeOtherType: coreClaim.CLAIM_CAUSE_OTHERS_TYPE || null,
            claimCause: coreClaim.CLAIM_CAUSE,
            createdAt: coreClaim.CREATED_AT ? String(coreClaim.CREATED_AT) : null,
            updatedAt: coreClaim.UPDATED_AT ? String(coreClaim.UPDATED_AT) : null,
            closedAt: coreClaim.CLOSED_AT ? String(coreClaim.CLOSED_AT) : null,
            inProcessAt: ClaimsMapper.toDateString(coreClaim.IN_PROCESS_AT),
            moreInfoNeededAt: ClaimsMapper.toDateString(coreClaim.MORE_INFO_NEEDED_AT),
            informationAddedAt: ClaimsMapper.toDateString(coreClaim.INFORMATION_ADDED_AT),
            rejectedAt: ClaimsMapper.toDateString(coreClaim.REJECTED_AT),
            canceledAt: ClaimsMapper.toDateString(coreClaim.CANCELED_AT),
            acceptedExpectCompensationAt: ClaimsMapper.toDateString(coreClaim.ACCEPTED_EXPECT_COMPENSATION_AT),
            acceptedPendingClientCompensationAt: ClaimsMapper.toDateString(
                coreClaim.ACCEPTED_PENDING_CLIENT_COMPENSATION_AT
            ),
            description: coreClaim.DESCRIPTION,
            eshopClaimId: coreClaim.ESHOP_CLAIM_ID,
            note: coreClaim.NOTE || null,
            resolutionText: coreClaim.RESOLUTION_TEXT || null,
            resolver: coreClaim.RESOLVER || null,
            status: coreClaim.STATUS || null,
            compensationCurrency: coreClaim.COMPENSATION_CURRENCY || null,
            goodsPrice: ClaimsMapper.parsePrice(coreClaim.GOODS_PRICE),
            totalServicePrice: ClaimsMapper.parsePrice(coreClaim.TOTAL_SERVICE_PRICE),
            transportPrice: ClaimsMapper.parsePrice(coreClaim.TRANSPORT_PRICE),
            eshopNote: coreClaim.ESHOP_NOTE
        };
    }
}

